import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAuthMember } from '../../../services/auth/member.service';
import { ApiDigitalAuthRole } from '../../../services/auth/role.service';
import { CompanyMember } from '../../../models/member.model';
import { Role } from '../../../models/role.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import AppPreloader from '../../../components/Preloader';

export interface MemberViewPageProps {};

const MemberViewPage: React.FunctionComponent<MemberViewPageProps> = props => {
  const {setRoute, appServiceForUser, companyForUser} = UseOutletContextAdmin()
  const {lang} = StateLanguage()
  const {id} = useParams()

  const [mounted, setMounted] = useState(false)
  const [member, setMember] = useState<CompanyMember | null>(null)
  const [allRoles, setAllRoles] = useState<Role[] | null>(null)

  const loadMember = async (api_key: string) => {
    await ApiDigitalAuthMember.getCompanyMembers(api_key).then((response) => {
      if (response.status === 200) {
        const members: CompanyMember[] = Array.isArray(response.data) ? response.data : []
        const found = members.find(m => m.id === id)
        setMember(found ?? null)
      } else {
        errorWebHandler(lang, {response})
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadRoles = async (api_key: string) => {
    const response = await ApiDigitalAuthRole.getRoles(api_key, true)
    if (response.status === 200 || response.status === 304) {
      setAllRoles(Array.isArray(response.data) ? response.data : [])
    } else {
      setAllRoles([])
    }
  }

  const handleAssignRole = async (role_id: string, role: Role) => {
    if (!appServiceForUser || !companyForUser || !member) return
    if (role.caller_can_assign === false) return

    await ApiDigitalAuthMember.assignRole(appServiceForUser.api_key, member.id, role_id).then((response) => {
      if (response.status === 200 || response.status === 201) {
        loadMember(appServiceForUser.api_key)
      } else {
        if (response.data?.code === 'INVITE_ROLE_FORBIDDEN') {
          GlobalSwal.fire({
            text: response.data.message || lang.labels.roleAssignmentForbidden,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        } else {
          errorWebHandler(lang, {response})
        }
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleUnassignRole = async (role_id: string, role_name: string) => {
    if (!appServiceForUser || !companyForUser || !member) return

    GlobalSwal.fire({
      title: lang.labels.confirmRemoveRole,
      text: `${lang.labels.removeRole}: ${role_name}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ApiDigitalAuthMember.unassignRole(appServiceForUser!.api_key, member!.id, role_id).then((response) => {
          if (response.status === 200) {
            loadMember(appServiceForUser!.api_key)
          } else {
            errorWebHandler(lang, {response})
          }
        }).catch((error) => {
          errorWebHandler(lang, error)
        })
      }
    })
  }

  const memberHasRole = (role_id: string): boolean => {
    if (!member || !member.roles) return false
    return member.roles.some(r => r.id === role_id)
  }

  useEffect(() => {
    if (appServiceForUser && companyForUser) {
      loadMember(appServiceForUser.api_key)
      loadRoles(appServiceForUser.api_key)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.member}, company: true})

    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { member && allRoles
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <div className="d-flex align-items-center">
                <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                  <div className="symbol-label fs-1 bg-light-primary text-primary">
                    {(member.first_name || member.email).charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="d-flex flex-column">
                  <h3 className="fw-bolder mb-0">
                    {member.first_name || member.last_name
                      ? `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim()
                      : member.email
                    }
                  </h3>
                  <span className="text-muted fs-7">{member.email}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="mb-8">
              <h5 className="fw-bolder mb-4">{lang.labels.assignedRoles}</h5>
              { member.roles && member.roles.length > 0
                ?
                <div className="d-flex flex-wrap gap-2">
                  { member.roles.map((role, index) => (
                    <span key={index} className={`badge ${role.is_system ? 'badge-light-info' : 'badge-light-primary'} fs-7 p-3`}>
                      {role.name}
                      {role.is_system && <span className="ms-1 text-info">({lang.labels.system})</span>}
                    </span>
                  ))}
                </div>
                :
                <div className="text-muted">{lang.labels.none}</div>
              }
            </div>
            <div className="separator my-5"></div>
            <h5 className="fw-bolder mb-4">{lang.labels.manageRoles}</h5>
            <div className="table-responsive">
              <table className="table align-middle table-row-dashed mb-2">
                <thead className="border-bottom">
                  <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                    <th className="min-w-200px">{lang.labels.role}</th>
                    <th className="min-w-100px">{lang.labels.type}</th>
                    <th className="w-100px text-end">{lang.labels.actions}</th>
                  </tr>
                </thead>
                <tbody className="text-secondary fw-bold">
                  { allRoles.map((role, index) => {
                    const assigned = memberHasRole(role.id)
                    const assignable = role.caller_can_assign !== false
                    return (
                      <tr key={index} className="row-dashed">
                        <td>
                          <span className="fw-bolder">{role.name}</span>
                          {role.description && <div className="text-muted fs-8">{role.description}</div>}
                        </td>
                        <td>
                          { role.is_system
                            ? <span className="badge badge-light-info fs-8">{lang.labels.system}</span>
                            : <span className="badge badge-light fs-8">Custom</span>
                          }
                        </td>
                        <td className="text-end">
                          { assigned
                            ?
                            <button className="btn btn-sm btn-light-danger" type="button" onClick={() => handleUnassignRole(role.id, role.name)}>
                              {lang.labels.removeRole}
                            </button>
                            : assignable
                              ?
                              <button className="btn btn-sm btn-light-success" type="button" onClick={() => handleAssignRole(role.id, role)}>
                                {lang.labels.assignRole}
                              </button>
                              :
                              <button className="btn btn-sm btn-light opacity-50" type="button" disabled title={lang.labels.roleNotAssignableByYou}>
                                {lang.labels.assignRole}
                              </button>
                          }
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default MemberViewPage;
